import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/common";
import DataTable from "@/components/DataTable";
import QuickFilters, { durationFilters } from "@/components/QuickFilters";
import BulkEdit, { selectColumn, useCanBulk, useIsAdmin, rowDeleteColumn } from "@/components/BulkEdit";
import RuleReassign from "@/components/RuleReassign";
import MultiSelect from "@/components/MultiSelect";
import { assignable, AssigneeCell, useUserMap } from "@/lib/assignees";
import { usePaged, useDebounced } from "@/lib/paged";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, PhoneForwarded, Pencil, Filter, Plus, X, Save, Star } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "name", label: "Name", type: "text" },
  { key: "mobile", label: "Mobile", type: "text" },
  { key: "city", label: "City", type: "text" },
  { key: "state", label: "State", type: "text" },
  { key: "course_interest", label: "Interested Course", type: "text" },
  { key: "exam", label: "Exam", type: "text" },
  { key: "rank", label: "Rank", type: "number" },
  { key: "status", label: "Status", type: "text" },
  { key: "assigned_name", label: "Assignee", type: "text" },
  { key: "campaign", label: "Campaign", type: "text" },
  { key: "source", label: "Source", type: "text" },
];
const OPS = {
  text: ["contains", "is", "is not", "is empty", "is not empty"],
  number: ["equals", "greater than", "less than", "is empty", "is not empty"],
};
const opsFor = (key) => OPS[FIELDS.find((f) => f.key === key)?.type || "text"];

function matchOne(row, c) {
  const raw = row[c.field];
  const v = (raw ?? "").toString().toLowerCase();
  const val = (c.value ?? "").toString().toLowerCase();
  switch (c.op) {
    case "contains": return v.includes(val);
    case "is": return v === val;
    case "is not": return v !== val;
    case "is empty": return v === "";
    case "is not empty": return v !== "";
    case "equals": return Number(raw) === Number(c.value);
    case "greater than": return Number(raw) > Number(c.value);
    case "less than": return Number(raw) < Number(c.value);
    default: return true;
  }
}

const VIEWS_KEY = "ohc_prospect_views";
const QF = [{ key: "city", label: "Cities" }, { key: "course_interest", label: "Courses" }, { key: "exam", label: "Exams" }, { key: "campaign", label: "Campaigns" }, { key: "source", label: "Sources" }, { key: "last_outcome", label: "Last Dispositions" }, { key: "tag", label: "Tags" }, ...durationFilters];

export default function Prospects() {
  const { config, user } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState([]);
  const [assignee, setAssignee] = useState([]);
  const [users, setUsers] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [match, setMatch] = useState("AND");
  const [showBuilder, setShowBuilder] = useState(false);
  const [views, setViews] = useState(() => { try { return JSON.parse(localStorage.getItem(VIEWS_KEY)) || []; } catch { return []; } });
  const [activeView, setActiveView] = useState("");
  const [editing, setEditing] = useState(null);
  const [quick, setQuick] = useState(null);
  const [sel, setSel] = useState([]);
  const canBulk = useCanBulk();

  const [params, setParams] = useSearchParams();
  const batch = params.get("batch") || "";
  const dq = useDebounced(search);
  const { rows: all, data: pageData, reload, total, complete } = usePaged("/prospects", { batch, status: status.join(","), assigned_name: assignee.join(","), search: dq.trim() });
  const [pageView, setPageView] = useState([]);
  const umap = useUserMap();
  const isAdmin = useIsAdmin();
  const load = () => reload();
  useEffect(() => { api.get("/users").then(({ data }) => setUsers(assignable(data))).catch(() => {}); }, []);

  const assignees = pageData?.assignees || [];
  const filtered = useMemo(() => (all || []).filter((r) => {
    const valid = conditions.filter((c) => c.field && c.op);
    if (valid.length) {
      const results = valid.map((c) => matchOne(r, c));
      return match === "AND" ? results.every(Boolean) : results.some(Boolean);
    }
    return true;
  }), [all, conditions, match]);

  const persistViews = (v) => { setViews(v); localStorage.setItem(VIEWS_KEY, JSON.stringify(v)); };
  const addCondition = () => setConditions([...conditions, { field: "name", op: "contains", value: "" }]);
  const setCond = (i, patch) => setConditions(conditions.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const removeCond = (i) => setConditions(conditions.filter((_, idx) => idx !== i));
  const clearAll = () => { setConditions([]); setActiveView(""); setStatus([]); setAssignee([]); setSearch(""); };
  const applyView = (v) => { setConditions(v.conditions || []); setMatch(v.match || "AND"); setActiveView(v.name); setShowBuilder(true); };
  const saveView = () => {
    const name = window.prompt("Save current filter as view. Name:", activeView || "My Filter");
    if (!name) return;
    const next = [...views.filter((v) => v.name !== name), { name, conditions, match }];
    persistViews(next); setActiveView(name); toast.success(`Saved view "${name}"`);
  };
  const deleteView = (name) => { persistViews(views.filter((v) => v.name !== name)); if (activeView === name) setActiveView(""); };

  const saveEdit = async () => {
    try {
      await api.patch(`/prospects/${editing.id}`, {
        name: editing.name, mobile: editing.mobile, alt_mobile: editing.alt_mobile, email: editing.email,
        city: editing.city, state: editing.state, course_interest: editing.course_interest,
        exam: editing.exam, rank: Number(editing.rank) || 0, status: editing.status, tag: editing.tag || "",
        assigned_to: editing.assigned_to || undefined,
      });
      toast.success("Prospect updated"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const columns = [
    selectColumn(pageView, sel, setSel, canBulk),
    { key: "name", label: "Prospect", fixed: true, render: (r) => <div><div className="font-semibold text-slate-800">{r.name}</div><div className="text-[11px] text-slate-400 font-mono">{r.mobile}</div></div> },
    { key: "city", label: "Location" },
    { key: "course_interest", label: "Interest", render: (r) => `${r.course_interest || "—"}` },
    { key: "exam", label: "Exam / Rank", render: (r) => <span className="text-xs">{r.exam} {r.rank ? `· ${r.rank}` : ""}</span> },
    { key: "rank", label: "Rank", hidden: true },
    { key: "last_call_duration", label: "Last Call Duration", hidden: true, render: (r) => (r.last_call_duration ? `${Math.floor(r.last_call_duration / 60)}m ${r.last_call_duration % 60}s` : "—") },
    { key: "talk_seconds", label: "Total Talk Time", hidden: true, render: (r) => (r.talk_seconds ? `${Math.floor(r.talk_seconds / 60)}m ${r.talk_seconds % 60}s` : "—") },
    { key: "campaign", label: "Campaign", render: (r) => <span className="text-xs text-slate-500">{r.campaign || "—"}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
    { key: "tag", label: "Tag", render: (r) => (r.tag ? <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800" data-testid={`prospect-tag-${r.id}`}>{r.tag}</span> : <span className="text-slate-300">—</span>) },
    { key: "assigned_name", label: "Assigned", render: (r) => <AssigneeCell id={r.assigned_to} name={r.assigned_name} map={umap} /> },
    { key: "attempts", label: "Attempts" },
    { key: "state", label: "State", hidden: true },
    { key: "source", label: "Source", hidden: true },
    { key: "last_outcome", label: "Last Disposition", hidden: true },
    { key: "email", label: "Email", hidden: true },
    { key: "actions", label: "", fixed: true, render: (r) => (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setEditing({ ...r })} className="w-8 h-8 rounded-lg hover:bg-indigo-50 flex items-center justify-center text-[#4F46E5]" title="Edit" data-testid={`edit-prospect-${r.id}`}><Pencil className="w-4 h-4" /></button>
      </div>) },
  ];

  return (
    <div>
      <PageHeader title="Prospects" subtitle="Bulk / cold-calling database — NOT counted as leads until qualified" testid="prospects-header">
        <Button variant="outline" onClick={() => setShowBuilder((s) => !s)} data-testid="prospects-filter-toggle"><Filter className="w-4 h-4 mr-1" /> Filters{conditions.length ? ` (${conditions.length})` : ""}</Button>
        <RuleReassign entity="prospects" sel={sel} visible={quick} onDone={() => { setSel([]); load(); }} fields={[["city", "City"], ["state", "State"], ["course_interest", "Course"], ["exam", "Exam"], ["rank", "Rank"], ["campaign", "Campaign"], ["status", "Status"], ["source", "Source"], ["language", "Language"], ["assigned_name", "Current owner"]]} />
        <Button onClick={() => {
          const narrowed = quick && (quick.length < (total ?? all?.length ?? 0) || conditions.length || status.length || assignee.length || dq.trim() || batch);
          if (narrowed && !quick.length) { toast.error("No prospects match the current filters"); return; }
          if (narrowed) sessionStorage.setItem("ohc_queue", JSON.stringify({ ids: quick.map((r) => r.id) }));
          navigate("/calling-queue");
        }} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="open-queue-btn"><PhoneForwarded className="w-4 h-4 mr-1" /> My Calling Queue</Button>
      </PageHeader>
      {batch && (
        <div className="mb-3 flex items-center gap-2 text-sm bg-indigo-50 border border-indigo-200 text-[#312E81] rounded-lg px-3 py-2" data-testid="prospects-batch-banner">
          Showing prospects from one import batch ({total})
          <button onClick={() => setParams({})} className="ml-auto text-xs underline" data-testid="prospects-batch-clear">Show all prospects</button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search all prospects: name / mobile / city" className="pl-9 bg-white" data-testid="prospects-search" />
        </div>
        <MultiSelect label="All Statuses" options={config?.prospect_statuses || []} value={status} onChange={setStatus} testid="prospects-status-filter" width="w-48" />
        <MultiSelect label="All Assignees" options={assignees} value={assignee} onChange={setAssignee} testid="prospects-assignee-filter" width="w-48" />
      </div>

      <QuickFilters rows={all === null ? null : filtered} fields={QF} onFiltered={setQuick} testid="prospects" />
      <BulkEdit entity="prospects" sel={sel} setSel={setSel} users={users} onDone={load} fields={[{ key: "status", label: "Status", options: ["New", "Assigned", "Contacted", "Callback", "Interested", "Not Interested", "Do Not Call"] }, { key: "campaign", label: "Campaign" }, { key: "city", label: "City" }, { key: "course_interest", label: "Course" }, { key: "exam", label: "Exam" }, { key: "source", label: "Source" }, { key: "tag", label: "Tag", options: config?.prospect_tags || [] }]} />

      {showBuilder && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 shadow-sm" data-testid="prospects-filter-builder">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#312E81]"><Filter className="w-4 h-4" /> Custom Filter Conditions</div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-500">Match</span>
              <Select value={match} onValueChange={setMatch}>
                <SelectTrigger className="h-8 w-24 bg-white" data-testid="filter-match"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="AND">ALL (AND)</SelectItem><SelectItem value="OR">ANY (OR)</SelectItem></SelectContent>
              </Select>
            </div>
          </div>

          {/* saved views */}
          {views.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {views.map((v) => (
                <span key={v.name} className={`inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs border cursor-pointer ${activeView === v.name ? "bg-indigo-50 border-indigo-300 text-[#4F46E5]" : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
                  <button onClick={() => applyView(v)} data-testid={`view-${v.name}`} className="flex items-center gap-1"><Star className="w-3 h-3" /> {v.name}</button>
                  <button onClick={() => deleteView(v.name)} className="w-4 h-4 rounded-full hover:bg-slate-200 flex items-center justify-center"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}

          <div className="space-y-2">
            {conditions.map((c, i) => {
              const type = FIELDS.find((f) => f.key === c.field)?.type || "text";
              const showVal = !["is empty", "is not empty"].includes(c.op);
              return (
                <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`filter-condition-${i}`}>
                  <Select value={c.field} onValueChange={(v) => setCond(i, { field: v, op: opsFor(v)[0] })}>
                    <SelectTrigger className="h-9 w-44 bg-white" data-testid={`filter-field-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{FIELDS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={c.op} onValueChange={(v) => setCond(i, { op: v })}>
                    <SelectTrigger className="h-9 w-40 bg-white" data-testid={`filter-op-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{opsFor(c.field).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                  {showVal && (c.field === "status"
                    ? <Select value={c.value} onValueChange={(v) => setCond(i, { value: v })}><SelectTrigger className="h-9 w-44 bg-white" data-testid={`filter-value-${i}`}><SelectValue placeholder="Value" /></SelectTrigger><SelectContent className="bg-white">{config?.prospect_statuses?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                    : <Input value={c.value} onChange={(e) => setCond(i, { value: e.target.value })} placeholder="Value" type={type === "number" ? "number" : "text"} className="h-9 w-44 bg-white" data-testid={`filter-value-${i}`} />)}
                  <button onClick={() => removeCond(i)} className="w-8 h-8 rounded-lg hover:bg-rose-50 text-rose-500 flex items-center justify-center"><X className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={addCondition} data-testid="add-condition-btn"><Plus className="w-4 h-4 mr-1" /> Add a new condition</Button>
            <Button size="sm" className="bg-[#4F46E5] hover:bg-[#4338CA]" onClick={saveView} data-testid="save-view-btn"><Save className="w-4 h-4 mr-1" /> Save as view</Button>
            <Button size="sm" variant="ghost" onClick={clearAll} data-testid="clear-filters-btn">Clear all</Button>
            <span className="text-xs text-slate-400 ml-auto">{filtered.length} of {total} prospects</span>
          </div>
        </div>
      )}

      <DataTable columns={[...columns, rowDeleteColumn("prospects", isAdmin, load)]} rows={quick} loading={all === null} testid="prospects-table" rowTestid="prospect-row" columnsKey="prospects" sortable paginate loadingMore={!complete} onPageRows={(r) => { setPageView(r); setSel([]); }} empty="No prospects found" />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Prospect</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-prospect-name" /></div>
              <div><Label>Mobile</Label><Input value={editing.mobile || ""} onChange={(e) => setEditing({ ...editing, mobile: e.target.value })} data-testid="edit-prospect-mobile" /></div>
              <div><Label>Alt Mobile</Label><Input value={editing.alt_mobile || ""} onChange={(e) => setEditing({ ...editing, alt_mobile: e.target.value })} /></div>
              <div className="col-span-2"><Label>Email</Label><Input value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></div>
              <div><Label>City</Label><Input value={editing.city || ""} onChange={(e) => setEditing({ ...editing, city: e.target.value })} data-testid="edit-prospect-city" /></div>
              <div><Label>State</Label><Input value={editing.state || ""} onChange={(e) => setEditing({ ...editing, state: e.target.value })} /></div>
              <div><Label>Interested Course</Label><Input value={editing.course_interest || ""} onChange={(e) => setEditing({ ...editing, course_interest: e.target.value })} /></div>
              <div><Label>Exam</Label><Input value={editing.exam || ""} onChange={(e) => setEditing({ ...editing, exam: e.target.value })} /></div>
              <div><Label>Rank</Label><Input type="number" value={editing.rank || ""} onChange={(e) => setEditing({ ...editing, rank: e.target.value })} /></div>
              <div><Label>Status</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="edit-prospect-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{config?.prospect_statuses?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Tag (optional)</Label>
                <Select value={editing.tag || "none"} onValueChange={(v) => setEditing({ ...editing, tag: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="edit-prospect-tag"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="none">No tag</SelectItem>{(config?.prospect_tags || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Assignee</Label>
                <Select value={editing.assigned_to || ""} onValueChange={(v) => setEditing({ ...editing, assigned_to: v })}>
                  <SelectTrigger data-testid="edit-prospect-assignee"><SelectValue placeholder="Unassigned / Pool" /></SelectTrigger>
                  <SelectContent className="bg-white">{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="save-edit-prospect-btn">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
